import {
    ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { getCategoryAttachments, getProductsId } from '../../../../store/category-page.selectors';
import { CategoriesGridStyle, GridElement, GridProductStyle } from '../../models/grid.models';

import { AttachmentCategory, Category } from '@core/domain/models/category.models';
import { getCmsConfigValue, getDebugBarShowProductsInfoStatus } from '@core/infrastructure/state/core.selectors';
import { MediasPublicConstants } from '@features/products/medias';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { AddToCartSheetModel } from '@shared/product';
import { CategoryConfiguration, HiddenFieldType, ProductViewConfig, ProductViewPlace, ProductViewPlaceValue } from '@shared/product/models';

@Component({
    selector: 'app-grid-studio',
    templateUrl: './grid-studio.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridStudioComponent implements OnInit {
    @Input() public defaultImageTypes: Map<number, string>;

    @Input() public category: Category;

    @Input() public isCategoryNew: boolean;

    @Input() public pageGridElements: GridElement[];

    @Input() public gridElements: GridElement[];

    @Input() public place: ProductViewPlace;

    @Input() public seoCategoryName: string;

    @Input() public showMediaCarousel: boolean;

    @Input() public showLastSizesGrid: boolean;

    @Output() public readonly loadNextPageEvent: EventEmitter<void> = new EventEmitter();

    private readonly categoriesGridStyle$ = this.store.select(getCmsConfigValue('background_color'));

    public isDebugShowProductsInfoActive$: Observable<boolean> = this.store.select(getDebugBarShowProductsInfoStatus);

    public categoryAttachments$: Observable<any> = this.store.select(getCategoryAttachments);

    public getAllProductsId$: Observable<number[]> = this.store.select(getProductsId);

    public categoryConfiguration$: Observable<CategoryConfiguration>;

    public seoCategoryStyle$: Observable<GridProductStyle> = this.categoriesGridStyle$.pipe(
        first(),
        map((categoriesGridStyle: CategoriesGridStyle[]) => this.setStyleColor(categoriesGridStyle))
    );

    public addToCartSheetModel: AddToCartSheetModel;

    public defaultTemplateAttachment = false;

    // El componente add-to-cart-bottom-sheet usa funcionalidad de product-view (actionAddToCartProductView) por lo que esto acaba siendo necesario
    public productViewConfig: ProductViewConfig = {
        place: ProductViewPlaceValue.PRODUCT_GRID,
        eventFrom: EventFrom.parrilla,
        location: MediasPublicConstants.PublicMediaLocationType.category_page,
        media: undefined,
    };

    // Template generica para el grid-last-sizes en vista studio
    public templateConfiguration: ProductViewConfig;

    constructor(
        private readonly store: Store,
    ) { }

    public onScrollStudio(): void {
        this.loadNextPageEvent.emit();
    }

    private changeConfigElGridElements(
        gridElements: GridElement[],
        attachments: AttachmentCategory[],
        productsIds: number[]
    ): CategoryConfiguration {
        const categoryConfiguration: CategoryConfiguration = {
            soldOut: false, productConf: new Map(), category: this.category, isCategoryNew: this.isCategoryNew,
        };

        if (!gridElements || gridElements.length === 0) {
            return categoryConfiguration;
        }

        categoryConfiguration.soldOut = !!attachments?.find(attachment => attachment.name === 'SOLD_OUT_CATEGORY');

        const ccIds = gridElements.reduce((acc, gridElement) => acc.concat(gridElement.ccIds), []);
        for (const ccId of ccIds) {
            if (productsIds.includes(ccId)) {
                const gridElement = gridElements.find(element => element.ccIds.includes(ccId));
                const hiddenField: HiddenFieldType = (gridElement?.hiddenFieldsByProduct || [])
                    .filter(({ name, productId }) => !!name && productId.includes(ccId))
                    .map(({ name }) => name as HiddenFieldType)
                    .shift();
                const defaultImageType = gridElement.commercialComponentIds?.find(
                    element=> element.ccId === ccId
                )?.defaultImageType;
                const productConf = {
                    hiddenField,
                    showNameUppercase: false,
                    style: gridElement?.style,
                    defaultImageType,
                };
                categoryConfiguration.productConf.set(ccId, productConf);
            }
        }

        return categoryConfiguration;
    }

    public ngOnInit(): void {
        this.categoryConfiguration$ = combineLatest([
            this.categoryAttachments$, this.getAllProductsId$,
        ]).pipe(
            first(),
            map(([attachments, productsIds]: [AttachmentCategory[], number[]]) => {
                this.defaultTemplateAttachment = attachments?.some(item => item.name === 'default_template');
                return this.changeConfigElGridElements(this.gridElements, attachments, productsIds);
            })
        );

        this.templateConfiguration = {
            media: {
                ratio: '3-4',
            },
            location: MediasPublicConstants.PublicMediaLocationType.ZOOM_2,
            place: this.place,
            clickable: true,
            discardDoubleVideo: true,
            discardFallback: true,
            showQuickly: true,
            showQuicklyMobile: true,
            showMediaCarousel: this.showMediaCarousel,
        };
    }

    private setStyleColor(categoriesGridStyle: CategoriesGridStyle[]): GridProductStyle {
        const idCategory = this.category.id;
        const categoryStyle = categoriesGridStyle.find(obj => obj.ID.includes(Number(idCategory)));

        return {
            tintColor: categoryStyle?.STYLE.fontColor,
            backgroundColor: categoryStyle?.STYLE.backgroundColor,
        };
    }

    public addToCartSheet(product) {
        this.addToCartSheetModel = {
            product,
            categoryId: this.category?.id,
            fullView: true,
        };
    }

    public closeAddToCartSheet() {
        this.addToCartSheetModel = null;
    }
}
